import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  BLOCKER_CATEGORIES,
  BLOCKER_CATEGORY_LABELS,
  type Blocker,
  type BlockerCategory,
  type DecidedField
} from '../types/conversationState';
import { resolveBlockerTemplate } from '../constants/blockerTemplates';
import BlockerChip from './input_support/BlockerChip';

type Props = {
  blockers: Blocker[];
  decidedFields: DecidedField[];
  searchTargets: string[];
  readyToSearch: boolean;
  isLoading: boolean;
  isArrangementRequested: boolean;
  hasConversationState: boolean;
  onChipClick: (template: string) => void;
  onBulkInsert: (template: string) => void;
};

const buildBulkInsertTemplate = (blockers: Blocker[]): string =>
  blockers
    .filter(b => b.type === 'missing_field')
    .map(b => resolveBlockerTemplate(b).template)
    .filter(t => t !== '')
    .join('\n');

const groupByCategory = (fields: DecidedField[]): Record<BlockerCategory, DecidedField[]> => {
  const result: Record<BlockerCategory, DecidedField[]> = { outward: [], homeward: [], hotel: [] };
  for (const f of fields) {
    result[f.category].push(f);
  }
  return result;
};

const InputSupportSection = ({
  blockers,
  decidedFields,
  searchTargets,
  readyToSearch,
  isLoading,
  isArrangementRequested,
  hasConversationState,
  onChipClick,
  onBulkInsert
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (!hasConversationState) return null;
  if (isArrangementRequested) return null;
  if (readyToSearch) return null;

  if (isLoading) {
    return <div style={styles.container}>不足情報を確認中...</div>;
  }

  const visibleBlockers = blockers.filter(b => searchTargets.includes(b.category));
  const visibleDecidedFields = decidedFields.filter(d => searchTargets.includes(d.category));

  if (visibleBlockers.length === 0 && visibleDecidedFields.length === 0) return null;

  const hasMissingField = visibleBlockers.some(b => b.type === 'missing_field');
  const groupedDecided = groupByCategory(visibleDecidedFields);
  const decidedCategories = BLOCKER_CATEGORIES.filter(c => groupedDecided[c].length > 0);

  return (
    <div style={styles.container}>
      {decidedCategories.length > 0 && (
        <section style={styles.decidedSection}>
          <div style={styles.heading}>
            <span style={styles.headingTitle}>AIが把握している条件</span>
          </div>
          {decidedCategories.map(category => (
            <div key={category} style={styles.decidedGroup}>
              <div style={styles.decidedGroupTitle}>{BLOCKER_CATEGORY_LABELS[category]}</div>
              <ul style={styles.decidedList}>
                {groupedDecided[category].map(field => (
                  <li key={field.key} style={styles.decidedItem}>
                    <span style={styles.decidedLabel}>{field.label}</span>
                    <span style={styles.decidedValue}>{field.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
      {visibleBlockers.length > 0 && (
        <section>
          <div style={styles.heading}>
            <span style={styles.headingTitle}>AIの提案に必要な情報</span>
          </div>
          <div style={styles.chips}>
            {visibleBlockers.map(blocker => (
              <BlockerChip key={`${blocker.key}-${blocker.type}`} blocker={blocker} onClick={onChipClick} />
            ))}
          </div>
          {hasMissingField && (
            <button
              type="button"
              style={styles.bulkButton}
              onClick={() => onBulkInsert(buildBulkInsertTemplate(visibleBlockers))}
            >
              まとめて入力
            </button>
          )}
        </section>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '14px'
  },
  heading: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: '8px',
    marginBottom: '6px'
  },
  headingTitle: {
    fontSize: '12px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  bulkButton: {
    marginTop: '10px',
    padding: '6px 12px',
    borderRadius: '4px',
    border: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#ffffff',
    color: theme.textColor,
    fontSize: '12px',
    cursor: 'pointer'
  },
  decidedSection: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '8px'
  },
  decidedGroup: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '4px'
  },
  decidedGroupTitle: {
    fontSize: '11px',
    fontWeight: 600 as const,
    color: theme.grayTextColor
  },
  decidedList: {
    listStyle: 'none' as const,
    margin: 0,
    padding: 0,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '2px'
  },
  decidedItem: {
    display: 'flex' as const,
    alignItems: 'baseline' as const,
    gap: '8px',
    fontSize: '12px'
  },
  decidedLabel: {
    color: theme.grayTextColor,
    minWidth: '88px'
  },
  decidedValue: {
    color: theme.textColor,
    fontWeight: 500 as const,
    flex: 1,
    wordBreak: 'break-all' as const
  }
});

export default InputSupportSection;
